import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

export function CreditManagementPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [customerId, setCustomerId] = useState('');

  const holds = useQuery({ queryKey: ['sales', 'credit-holds'], queryFn: () => salesApi.creditHolds().then((r) => r.items) });
  const customers = useQuery({ queryKey: ['sales', 'customers'], queryFn: () => salesApi.listCustomers({ limit: 200 }).then((r) => r.items) });
  const nameOf = useMemo(() => Object.fromEntries((customers.data || []).map((c) => [c.id, c.name])), [customers.data]);
  const credit = useQuery({ queryKey: ['sales', 'customer-credit', customerId], queryFn: () => salesApi.customerCredit(customerId).then((r) => r.items), enabled: Boolean(customerId) });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sales', 'credit-holds'] });
    queryClient.invalidateQueries({ queryKey: ['sales', 'customer-credit'] });
    queryClient.invalidateQueries({ queryKey: ['sales', 'orders'] });
  };
  const release = useMutation({ mutationFn: (id) => salesApi.releaseCredit(id), onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message) });
  const reject = useMutation({ mutationFn: (id) => salesApi.rejectCredit(id), onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message) });

  const rows = holds.data || [];

  const columns = [
    { key: 'number', label: t('cm.col.order'), render: (r) => <span className="mono">{r.orderNumber}</span> },
    { key: 'customer', label: t('cm.col.customer'), render: (r) => nameOf[r.customerId] || r.customerId },
    { key: 'amount', label: t('cm.col.amount'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.totalAmount)}</span> },
    { key: 'date', label: t('cm.col.date'), render: (r) => formatDate(r.orderDate) },
    { key: 'actions', label: '', align: 'right', render: (r) => (
      <span style={{ display: 'inline-flex', gap: 'var(--space-1)' }}>
        <Button size="sm" variant="ghost" disabled={reject.isPending} onClick={() => reject.mutate(r.id)}>{t('cm.action.reject')}</Button>
        <Button size="sm" variant="primary" disabled={release.isPending} onClick={() => release.mutate(r.id)}>{t('cm.action.release')}</Button>
      </span>
    ) },
  ];

  const c = credit.data;
  const pct = c && !c.unlimited && Number(c.creditLimit) > 0 ? Math.min(100, Math.round((Number(c.exposure) / Number(c.creditLimit)) * 100)) : 0;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('cm.title')}</h1>
          <p className="muted">{t('cm.subtitle')}</p>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('cm.metric.holds')} value={String(rows.length)} trendVariant={rows.length > 0 ? 'warning' : 'success'} />
        <MetricCard label={t('cm.metric.blocked')} value={formatCurrency(rows.reduce((s, r) => s + Number(r.totalAmount || 0), 0))} />
      </div>

      {error && <div className="alert danger" style={{ margin: 'var(--space-2) 0' }}>{error}</div>}

      <div className="settings-divider">{t('cm.holds')}</div>
      <Table columns={columns} rows={rows} isLoading={holds.isLoading} emptyMessage={t('cm.empty')} />

      <div className="settings-divider">{t('cm.lookup')}</div>
      <div className="card" style={{ padding: 'var(--space-3)' }}>
        <label className="field" style={{ maxWidth: 320 }}>
          <span className="field-label">{t('cm.col.customer')}</span>
          <select className="input" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">{t('cm.selectCustomer')}</option>
            {(customers.data || []).map((cust) => <option key={cust.id} value={cust.id}>{cust.code} · {cust.name}</option>)}
          </select>
        </label>
        {c && (
          <div className="form-stack" style={{ gap: 'var(--space-1)', marginTop: 'var(--space-3)', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="muted">{t('cm.limit')}</span><span className="mono num">{c.unlimited ? t('cm.unlimited') : formatCurrency(c.creditLimit)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="muted">{t('cm.exposure')}</span><span className="mono num">{formatCurrency(c.exposure)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="muted">{t('cm.available')}</span><span className="mono num">{c.unlimited ? '-' : formatCurrency(c.available)}</span></div>
            {!c.unlimited && (
              <div style={{ height: 8, background: 'var(--color-border)', borderRadius: 4, overflow: 'hidden', marginTop: 4 }}>
                <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? 'var(--color-danger, #dc2626)' : pct >= 85 ? 'var(--color-warning, #d97706)' : 'var(--color-success, #059669)' }} />
              </div>
            )}
            <div style={{ marginTop: 'var(--space-2)' }}>
              {c.unlimited ? <Badge variant="info">{t('cm.unlimited')}</Badge> : c.withinLimit ? <Badge variant="success">{t('cm.within')}</Badge> : <Badge variant="danger">{t('cm.exceeded')}</Badge>}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
